/**
 * React Query Client Configuration
 * 
 * This file provides the query client instance and core query key factories.
 * Plugin-specific query keys should be defined within their respective plugins.
 */

import { QueryClient } from '@tanstack/react-query';

// Create a single query client instance for the application
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Core Query Keys
 * 
 * These are the foundational query key factories for core system functionality.
 * Plugin-specific query keys should be defined within their respective plugins.
 */
export const queryKeys = {
  // User & Auth
  user: {
    all: ['user'] as const,
    current: () => [...queryKeys.user.all, 'current'] as const,
    config: () => [...queryKeys.user.all, 'config'] as const,
    preferences: () => [...queryKeys.user.all, 'preferences'] as const,
    list: () => [...queryKeys.user.all, 'list'] as const,
    routes: () => [...queryKeys.user.all, 'routes'] as const,
  },

  // Graph (core visualization)
  graph: {
    all: ['graph'] as const,
    data: () => [...queryKeys.graph.all, 'data'] as const,
    nodes: () => [...queryKeys.graph.all, 'nodes'] as const,
    edges: () => [...queryKeys.graph.all, 'edges'] as const,
    layout: () => [...queryKeys.graph.all, 'layout'] as const,
  },

  // Chat (core AI interaction)
  chat: {
    all: ['chat'] as const,
    sessions: () => [...queryKeys.chat.all, 'sessions'] as const,
    session: (id: string) => [...queryKeys.chat.sessions(), id] as const,
    messages: (sessionId: string) => [...queryKeys.chat.session(sessionId), 'messages'] as const,
  },

  // Plugins
  plugins: {
    all: ['plugins'] as const,
    list: () => [...queryKeys.plugins.all, 'list'] as const,
    installed: () => [...queryKeys.plugins.all, 'installed'] as const,
    available: () => [...queryKeys.plugins.all, 'available'] as const,
    detail: (id: string) => [...queryKeys.plugins.all, 'detail', id] as const,
  },

  // System
  system: {
    all: ['system'] as const,
    health: () => [...queryKeys.system.all, 'health'] as const,
    status: () => [...queryKeys.system.all, 'status'] as const,
    config: () => [...queryKeys.system.all, 'config'] as const,
  },

  // Smart Search
  smartSearch: {
    all: ['smart-search'] as const,
    adapters: () => [...queryKeys.smartSearch.all, 'adapters'] as const,
  },
} as const;

export default queryClient;
